import MuiLink from '@mui/material/Link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  ControlledFileUpload,
  ControlledTextField,
  ErrorSummary,
  ExpandableTable,
  ModalWrapper,
  SectionTitle,
  SegmentedControl,
} from 'modules/shared/components';
import { ensureFileId } from 'modules/shared/service';
import { capitalizeString } from 'modules/shared/utils';
import { logError } from 'utils/logger';
import { FEEDBACK_OUTCOMES, getAdminFeedback, takeFeedbackAction } from '../feedback.service';

const EMPTY_MESSAGES = {
  active: 'No active feedbacks',
  closed: 'No closed feedbacks',
};

const EMPTY_FEEDBACK = { pending: [], closed: [] };

const PRIMARY_VIEW_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Closed', value: 'closed' },
];

const DEFAULT_WARNING_LETTER = {
  email: '',
  subject: '',
  message: '',
  warningLetter: null,
};

const CLOSED_OUTCOME_LABELS = {
  [FEEDBACK_OUTCOMES.RESOLVED]: 'Resolved:',
  [FEEDBACK_OUTCOMES.VERBAL_ACTION]: 'Verbal action taken:',
  [FEEDBACK_OUTCOMES.WARNING_LETTER]: 'Warning letter issued:',
};

const FEEDBACK_ACTIONS = {
  RESOLVE: 'resolve',
  VERBAL: 'verbal-action',
  WARNING: 'warning-letter',
};

export default function AdminFeedback({ shouldRefresh, setShouldRefresh }) {
  const [feedbackByStatus, setFeedbackByStatus] = useState(EMPTY_FEEDBACK);
  const [actionMode, setActionMode] = useState(null); // {type: 'warning-letter, feedback}

  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  const [view, setView] = useState('active');

  const methods = useForm({
    defaultValues: DEFAULT_WARNING_LETTER,
  });
  const { reset, setValue } = methods;

  useEffect(() => {
    if (actionMode && actionMode?.type === FEEDBACK_ACTIONS.WARNING && actionMode?.feedback) {
      const feedbackItem = actionMode.feedback;
      setValue('email', feedbackItem.email);
      setValue('message', feedbackItem.message);
      setValue('subject', feedbackItem.subject);
    }
  }, [actionMode, setValue]);

  const fetchAllFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminFeedback();
      setFeedbackByStatus(data);
      setView('active');
      setShouldRefresh(false);
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to fetch feedbacks',
          error: err,
          code: 'ADMIN_FEEDBACK_FETCH_FAILED',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [setShouldRefresh]);

  useEffect(() => {
    fetchAllFeedbacks();
  }, [fetchAllFeedbacks]);

  useEffect(() => {
    if (shouldRefresh) fetchAllFeedbacks();
  }, [fetchAllFeedbacks, shouldRefresh]);

  useEffect(() => {
    setActionMode(null);
  }, [view]);

  const visibleFeedbacks = useMemo(() => {
    if (view === 'active') return feedbackByStatus.pending;
    return feedbackByStatus.closed;
  }, [feedbackByStatus.closed, feedbackByStatus.pending, view]);

  const emptyMessage = view === 'active' ? EMPTY_MESSAGES.active : EMPTY_MESSAGES.closed;

  const columns = useMemo(
    () => [
      { id: 'date', label: 'Date', dataKey: 'date', sortable: true },
      { id: 'name', label: 'Name', dataKey: 'employeeName', sortable: true },
      {
        id: 'department',
        label: 'Department',
        dataKey: 'department',
        sortable: true,
        renderCell: capitalizeString,
      },
    ],
    [],
  );

  const handleResolve = async (id, action) => {
    setResolving(true);
    try {
      await takeFeedbackAction(id, { resolutionType: action });
      const successMessage =
        action === FEEDBACK_ACTIONS.VERBAL
          ? 'Verbal action recorded'
          : 'Feedback resolved successfully';
      toast.success(successMessage);
      await fetchAllFeedbacks();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to resolve feedback',
          error: err,
          code: 'FEEDBACK_RESOLUTION_FAILED',
        }),
      );
    } finally {
      setResolving(false);
    }
  };

  const handleWarningLetterSubmit = async (data) => {
    setResolving(true);
    try {
      const uploadedFileId = await ensureFileId(data.warningLetter);
      if (!uploadedFileId) {
        throw new Error('WARNING_LETTER_FILE_MISSING');
      }

      await takeFeedbackAction(actionMode.feedback.id, {
        resolutionType: FEEDBACK_ACTIONS.WARNING,
        warningLetterId: uploadedFileId,
      });
      toast.success('Warning letter issued successfully');
      reset(DEFAULT_WARNING_LETTER);
      setActionMode(null);
      await fetchAllFeedbacks();
    } catch (err) {
      toast.error(
        logError({
          message:
            err.message === 'WARNING_LETTER_FILE_MISSING'
              ? 'Warning letter file is required'
              : 'Failed to issue warning letter',
          error: err,
          code: 'WARNING_LETTER_FAILED',
        }),
      );
    } finally {
      setResolving(false);
    }
  };

  const renderPendingFeedback = (feedback) => (
    <div className='row gap-3 self-end'>
      <button
        type='button'
        className='btn cta-outlined-btn'
        disabled={resolving}
        onClick={() => handleResolve(feedback.id, FEEDBACK_ACTIONS.RESOLVE)}
      >
        Resolve
      </button>
      <button
        type='button'
        className='btn cta-outlined-btn'
        disabled={resolving}
        onClick={() => handleResolve(feedback.id, FEEDBACK_ACTIONS.VERBAL)}
      >
        Take Verbal Action
      </button>
      <button
        type='button'
        className='btn cta-outlined-btn'
        disabled={resolving}
        onClick={() => setActionMode({ type: FEEDBACK_ACTIONS.WARNING, feedback })}
      >
        Issue Warning Letter
      </button>
    </div>
  );

  const renderClosedFeedback = (feedback) => (
    <div className='row row--between'>
      <div className='row gap-3'>
        <span>{CLOSED_OUTCOME_LABELS[feedback.outcome] ?? 'Closed:'}</span>
        <span className='text--secondary'>{feedback.actionDate}</span>
      </div>
      {feedback.warningLetter && (
        <MuiLink
          href={feedback.warningLetter}
          target='_blank'
          rel='noopener noreferrer'
          sx={{ textDecoration: 'underline' }}
        >
          preview letter
        </MuiLink>
      )}
    </div>
  );

  return (
    <>
      <div className='column gap-4'>
        <SegmentedControl
          options={PRIMARY_VIEW_OPTIONS}
          value={view}
          onChange={(v: string) => setView(v)}
          ariaLabel='Feedback view'
        />

        <ExpandableTable
          columns={columns}
          data={visibleFeedbacks}
          loading={loading}
          emptyMessage={emptyMessage}
          headerBg
          renderExpandedRow={(feedback) => (
            <div className='column'>
              <div>
                <span className='font-medium'>Subject: </span>
                <span>{feedback.subject}</span>
              </div>
              <div className='pre-styled-text'>{feedback.message}</div>
              {feedback.status === 'pending'
                ? renderPendingFeedback(feedback)
                : renderClosedFeedback(feedback)}
            </div>
          )}
        />
      </div>
      <WarningLetterModal
        open={actionMode?.type === FEEDBACK_ACTIONS.WARNING}
        methods={methods}
        onCancel={() => {
          setActionMode(null);
          reset(DEFAULT_WARNING_LETTER);
        }}
        onSubmit={handleWarningLetterSubmit}
        saving={resolving}
      />
    </>
  );
}

function WarningLetterModal({ open, methods, onCancel, onSubmit, saving }) {
  const { watch } = methods;
  const warningLetter = watch('warningLetter');
  const warningLetterFile = warningLetter?.file;
  const letterPreviewUrl = useMemo(() => {
    if (!(warningLetterFile instanceof File)) return null;
    return URL.createObjectURL(warningLetterFile);
  }, [warningLetterFile]);

  useEffect(() => {
    return () => {
      if (letterPreviewUrl) URL.revokeObjectURL(letterPreviewUrl);
    };
  }, [letterPreviewUrl]);

  return (
    <ModalWrapper open={open} onClose={onCancel}>
      <FormProvider {...methods}>
        <form className='column gap-4' onSubmit={methods.handleSubmit(onSubmit)}>
          <SectionTitle title='Warning Letter' />

          <ErrorSummary />

          <ControlledTextField id='email' name='email' placeholder='Enter email address' disabled />

          <div className='column gap-1'>
            <ControlledTextField id='subject' name='subject' placeholder='Enter subject' disabled />

            <ControlledTextField
              id='message'
              name='message'
              placeholder='Enter content'
              rules={{
                maxLength: { value: 1000, message: 'Feedback cannot exceed 1000 characters' },
              }}
              isMultiline
              rows={5}
              showCharCount
              disabled
            />
          </div>

          <span className='row gap-3'>
            <ControlledFileUpload
              name='warningLetter'
              rules={{ required: 'Warning Letter is required' }}
              autoUpload={false}
              buttonText='Attach file'
              buttonIcon='ion:attach-outline'
              buttonClass='btn text-btn text-btn--medium underline'
            />
            {letterPreviewUrl && (
              <MuiLink
                href={letterPreviewUrl}
                target='_blank'
                rel='noreferrer'
                sx={{ textDecoration: 'underline' }}
              >
                view
              </MuiLink>
            )}
          </span>
          <div className='row row--end gap-3'>
            <button type='button' className='btn outlined-btn' onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <button type='submit' className='btn cta-btn' disabled={saving}>
              Save
            </button>
          </div>
        </form>
      </FormProvider>
    </ModalWrapper>
  );
}
